import {
  createAsyncThunk,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";
import api from "../../app/api";
import { infoSnack, successSnack, errorSnack } from "../snackbar/snackbarSlice";

import { uuidv4 } from "../../app/utils";
import baseURL from "../../app/utils.js";

export const fetchClient = createAsyncThunk(
  "clients/fetchClient",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      const response = await api.get(
        `${baseURL}/v1/admin/client`,
        config
      );
      return await response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error fetching Client data"
          )
        );
      }

      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const fetchClientByID = createAsyncThunk(
  "clients/fetchClientByID",
  async (args, thunkAPI) => {
    const { token, clientId } = args;
    const config = {
      headers: { 
        Authorization: `Bearer ${token}`,
        'X-Request-ID': uuidv4()
      },
    };
    try {
      const response = await api.get(
        `${baseURL}/v1/admin/clients/${clientId}`,
        config
      );
      return await response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error fetching Client data by ID"
          )
        );
      }

      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const fetchClientCountry = createAsyncThunk(
  "clients/fetchClientCountry",
  async (args, thunkAPI) => {
    const config = {
      headers: { 'X-Client-Host': process.env.REACT_APP_LOCAL_URL, },
    };
    try {
      const response = await api.get(
        `${baseURL}/v1/rt/clients/country`,
        config
      );
      return await response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error fetching Client data"
          )
        );
      }

      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const updateClient = createAsyncThunk(
  "clients/updateClient",
  async (args, thunkAPI) => {
    const { token, clientData, clientId } = args;
    const config = {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    };
    
    try {
      const response = await api.patch(
        `${baseURL}/v1/admin/clients/${clientId}`,
        clientData,
        config
      );
      thunkAPI.dispatch(successSnack("Configuration updated successfully"));
      return await response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error updating Client data"
          )
        );
      }

      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

const clientsSlice = createSlice({
  name: "programs",
  initialState: {
    client: {},    
    country: {},
    loading: "idle",
    message: "",
    error: "",
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchClient.pending, (state) => {
      state.client = {};
      state.loading = "loading";      
    });
    builder.addCase(fetchClient.fulfilled, (state, { payload }) => {
      state.client = payload;
      state.loading = "loaded";
      state.editing = true;
    });
    builder.addCase(fetchClient.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    
    // New handler for fetchClientByID
    builder.addCase(fetchClientByID.pending, (state) => {
      state.client = {};
      state.loading = "loading";      
    });
    builder.addCase(fetchClientByID.fulfilled, (state, { payload }) => {
      state.client = payload;
      state.loading = "loaded";
      state.editing = true;
    });
    builder.addCase(fetchClientByID.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    
    builder.addCase(fetchClientCountry.pending, (state) => {
      state.country = {};
      state.loading = "loading";      
    });
    builder.addCase(fetchClientCountry.fulfilled, (state, { payload }) => {
      state.country = payload;
      state.loading = "loaded";
      state.editing = true;
    });
    builder.addCase(fetchClientCountry.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    
    builder.addCase(updateClient.pending, (state) => {
      state.loading = "loading";
    });
    builder.addCase(updateClient.fulfilled, (state, { payload }) => {
      // state.client = payload;
      state.loading = "loaded";
      state.editing = true;
    });
    builder.addCase(updateClient.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
  },
});

// Updated selector to include selectedClient
export const selectClients = createSelector(
  (state) => ({    
    client: state.clients.client,    
    country: state.clients.country,
    loading: state.clients.loading,
    message: state.clients.message,
    error: state.clients.error,
  }),
  (state) => state
);

export default clientsSlice.reducer;